import { useGlobalContext } from "../../../../../Context/Context";
import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import Button from "../../../../Custom/Button/Button";
import TabelaElektroOprema from "./TabelaElektroOprema";
import PreviewEOprema from "./PreviewElektroOprema";

const styles = {
  button: { width: "33%", height: "40px", cursor: "pointer" },
};

function ElektroOprema() {
  const [currentState, setCurrentState] = useState("AKTIVNA");
  const [sredstvoId, setSredstvoId] = useState("");
  const [masine, setMasine] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const {
    dictionary,
    baseUrl,
    apiKey,
    loggedUser,
    sredstvaOpremaPretraga,
    setSredstvaOpremaPretraga,
    setSelectedSredstvaOprema,
  } = useGlobalContext();

  // Mapiranje: levi panel → backend state
  const mapViewToStatus = (mode) => {
    if (mode === "AKTIVNA") return "AKTIVNA";
    if (mode === "ARHIVIRANE") return "NEAKTIVNA";
    if (mode === "TUDJE") return "TUDJA";
    return "AKTIVNA";
  };

  // Mapiranje: backend state → levi panel
  const mapStatusToView = (status) => {
    if (status === "AKTIVNA") return "AKTIVNA";
    if (status === "NEAKTIVNA") return "ARHIVIRANE";
    if (status === "TUDJA") return "TUDJE";
    return "AKTIVNA";
  };

  // SLUŠAMO EVENT IZ LEVOG MENIJA
  useEffect(() => {
    const onModeChange = (e) => {
      const mode = e?.detail?.mode;
      if (!mode) return;

      setSelectedRows([]);
      setSredstvoId("");

      const nextStatus = mapViewToStatus(mode);
      setCurrentState(nextStatus);
    };

    window.addEventListener("machinesViewChange", onModeChange);
    return () => window.removeEventListener("machinesViewChange", onModeChange);
  }, []);

  // Kad god se promeni currentState, sinhronizuj “utisnuti” taster levo
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("machinesViewSync", {
        detail: { mode: mapStatusToView(currentState) },
      }),
    );
  }, [currentState]);

  const handleNazad = () => {
    setSredstvoId("");
    setSelectedSredstvaOprema(0);
    setSredstvaOpremaPretraga(null);
    setSelectedRows([]);
    setCurrentState("AKTIVNA");
  };

  const fetchMasine = useCallback(async () => {
    if (!loggedUser?.id || !apiKey) return;
    setLoading(true);
    try {
      const payload = {
        user_id: loggedUser.id,
        api_key: apiKey,
        company_id: 1,
        status_filter: currentState,
      };
      const { data } = await axios.post(
        `${baseUrl}/machines/read_machines.php`,
        payload,
      );
      if (data.status === 200) {
        setMasine(data.data || []);
      } else {
        setMasine([]);
      }
    } catch (error) {
      setMasine([]);
    } finally {
      setLoading(false);
    }
  }, [currentState, loggedUser, apiKey, baseUrl]);

  useEffect(() => {
    window.addEventListener("machineAdded", fetchMasine);
    return () => window.removeEventListener("machineAdded", fetchMasine);
  }, [fetchMasine]);

  useEffect(() => {
    fetchMasine();
  }, [fetchMasine]);

  const filtrovaneMasine = useMemo(() => {
    if (!sredstvaOpremaPretraga) return masine;

    let filtered = [...masine];

    if (
      sredstvaOpremaPretraga.vrsta?.id &&
      sredstvaOpremaPretraga.vrsta.id !== "sve" &&
      sredstvaOpremaPretraga.vrsta.id !== -1
    ) {
      filtered = filtered.filter((m) => m.tip === sredstvaOpremaPretraga.vrsta.id);
    }

    if (
      sredstvaOpremaPretraga.poslovnaJedinica?.id &&
      sredstvaOpremaPretraga.poslovnaJedinica.id !== "sve" &&
      sredstvaOpremaPretraga.poslovnaJedinica.id !== -1
    ) {
      filtered = filtered.filter(
        (m) => m.poslovna_jedinica_id == sredstvaOpremaPretraga.poslovnaJedinica.id,
      );
    }

    if (
      sredstvaOpremaPretraga.istekDokumenta?.id &&
      sredstvaOpremaPretraga.istekDokumenta.id !== "sve" &&
      sredstvaOpremaPretraga.istekDokumenta.id !== -1
    ) {
      const istekId = sredstvaOpremaPretraga.istekDokumenta.id;
      filtered = filtered.filter((m) => {
        const statusi = [m.td, m.sn, m.rg, m.pu, m.od].filter(Boolean);
        if (istekId === "1") return statusi.includes("wrong");
        if (istekId === "2") return statusi.includes("notif");
        if (istekId === "3") return !statusi.includes("wrong") && !statusi.includes("notif");
        return true;
      });
    }

    if (sredstvaOpremaPretraga.pretraga) {
      const searchTerm = sredstvaOpremaPretraga.pretraga.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          (m.naziv && m.naziv.toLowerCase().includes(searchTerm)) ||
          (m.identifikacioni_broj &&
            m.identifikacioni_broj.toLowerCase().includes(searchTerm)),
      );
    }

    return filtered;
  }, [masine, sredstvaOpremaPretraga]);

  const handleArchiveSelected = async () => {
    if (!selectedRows.length) return;

    if (!window.confirm(`Arhivirati ${selectedRows.length} stavki?`)) return;

    try {
      await Promise.all(
        selectedRows.map((masina_id) =>
          axios.post(`${baseUrl}/machines/archive_machine.php`, {
            user_id: loggedUser.id,
            api_key: apiKey,
            masina_id,
          }),
        ),
      );

      setSelectedRows([]);
      window.dispatchEvent(new Event("machineAdded"));
    } catch (e) {
      console.error(e);
      alert("Greška pri arhiviranju.");
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          padding: "25px",
          backgroundColor: "#f9f9f9",
          height: "85%",
          overflow: "auto",
          border: "1.5px solid #9f9f9f",
        }}
      >
        {!sredstvoId && (
          <>
            {loading ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                Učitavanje...
              </div>
            ) : (
              <TabelaElektroOprema
                data={filtrovaneMasine}
                setSredstvoId={setSredstvoId}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
              />
            )}
          </>
        )}

        {sredstvoId && (
          <PreviewEOprema
            sredstvoId={sredstvoId}
            setSredstvoId={setSredstvoId}
          />
        )}
      </div>

      <div
        className="tasteri-tabele"
        style={{
          display: "flex",
          justifyContent: "space-between",
          height: "50px",
        }}
      >
        <div
          className="taster-tabele-left"
          style={{
            display: "flex",
          }}
        >
          <Button
            style={{ width: "230px", fontWeight: "bold", marginRight: "20px" }}
          >
            ШТАМПАЈ
          </Button>
          <Button style={{ width: "230px", fontWeight: "bold" }}>ПОШАЉИ</Button>
        </div>

        <div
          className="taster-tabele-right"
          style={{
            display: "flex",
          }}
        >
          {selectedRows.length > 0 && (
            <Button
              style={{ width: "230px", fontWeight: "bold", marginRight: "20px" }}
              onClick={handleArchiveSelected}
            >
              АРХИВИРАЈ
            </Button>
          )}

          <div onClick={handleNazad} style={{ height: "100%" }}>
            <Button style={{ width: "230px", fontWeight: "bold" }}>
              НАЗАД
            </Button>{" "}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ElektroOprema;